import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Credits from './pages/Credits';
import PendingCredits from './pages/PendingCredits';
import Debits from './pages/Debits';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import Profile from './pages/Profile';
import { Toaster } from 'react-hot-toast';

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/" />;
  }
  
  return children;
}

function RoleBasedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!allowedRoles.includes(user?.role)) {
    // If member tries to access non-reports, redirect to reports
    if (user?.role === 'member') {
      return <Navigate to="/reports" />;
    }
    // For other unauthorized roles, go to dashboard
    return <Navigate to="/" />;
  }
  
  return children;
}

function AppRoutes() {
  const { isMember } = useAuth();
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      
      {/* Protected Routes */}
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<RoleBasedRoute allowedRoles={['administrator', 'editor']}><Dashboard /></RoleBasedRoute>} />
        <Route path="credits" element={<RoleBasedRoute allowedRoles={['administrator', 'editor']}><Credits /></RoleBasedRoute>} />
        <Route path="credits/pending" element={<RoleBasedRoute allowedRoles={['administrator', 'editor']}><PendingCredits /></RoleBasedRoute>} />
        <Route path="debits" element={<RoleBasedRoute allowedRoles={['administrator', 'editor']}><Debits /></RoleBasedRoute>} />
        <Route path="reports" element={<RoleBasedRoute allowedRoles={['administrator', 'editor', 'member']}><Reports memberOnly={isMember} /></RoleBasedRoute>} />
        <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="profile" element={<RoleBasedRoute allowedRoles={['administrator', 'editor']}><Profile /></RoleBasedRoute>} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            borderRadius: '0.75rem',
            padding: '1rem',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
